import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { FiltroConfig, FiltrosAtivos } from './filtro-tabela.interface';

@Component({
    selector: 'sume-filtro-tabela',
    templateUrl: './filtro-tabela.component.html',
})
export class FiltroTabelaComponent implements OnInit, OnChanges, OnDestroy {
    /** Dados brutos a serem filtrados */
    @Input() dados: any[] = [];

    /** Configuração dos filtros a exibir */
    @Input() filtros: FiltroConfig[] = [];

    /** Mensagem exibida no tooltip de ajuda (opcional) */
    @Input() tooltipMensagem?: string;

    /** Emite a lista filtrada sempre que um filtro muda */
    @Output() dadosFiltrados = new EventEmitter<any[]>();

    /** Emite o mapa de valores ativos (id → valor) sempre que um filtro muda */
    @Output() filtrosAtivos = new EventEmitter<FiltrosAtivos>();

    /** FormControls indexados pelo id do filtro */
    controles: Record<string, FormControl> = {};

    /** Opções derivadas automaticamente dos dados (para opcoes: 'auto') */
    opcoesDerivadas: Record<string, any[]> = {};

    private subs: Subscription[] = [];

    ngOnInit(): void {
        this.inicializarControles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filtros'] && !changes['filtros'].firstChange) {
            this.inicializarControles();
            return;
        }
        if (changes['dados'] && !changes['dados'].firstChange) {
            this.derivarOpcoes();
            this.aplicarFiltros();
        }
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    getOpcoes(filtro: FiltroConfig): any[] {
        if (filtro.opcoes === 'auto') return this.opcoesDerivadas[filtro.id] || [];
        return filtro.opcoes || [];
    }

    /** Retorna as opções de um balão com sua contagem nos dados brutos */
    getOpcoesBalaoBadge(filtro: FiltroConfig): { opcao: string; count: number }[] {
        const fonte = this.dados ?? [];
        return this.getOpcoes(filtro).map(opcao => ({
            opcao: String(opcao),
            count: fonte.filter(
                item => String(this.extrairCampoValor(item, filtro)) === String(opcao)
            ).length,
        }));
    }

    /** Contagem total para o pill "Tudo" */
    getTudoCount(): number {
        return (this.dados ?? []).length;
    }

    /** Classe CSS do container do filtro (balão ocupa col-12 por padrão) */
    getColClass(filtro: FiltroConfig): string {
        if (filtro.colClass) return filtro.colClass;
        return filtro.tipo === 'balão' ? 'col-12' : 'col-md-12 col-lg-3';
    }

    private inicializarControles(): void {
        this.subs.forEach(s => s.unsubscribe());
        this.subs = [];
        this.controles = {};

        this.filtros.forEach(filtro => {
            const valorInicial = filtro.tipo === 'checkbox' ? false : '';
            this.controles[filtro.id] = new FormControl(valorInicial);
        });

        this.derivarOpcoes();
        this.configurarSubscriptions();
        this.aplicarFiltros();
    }

    private derivarOpcoes(): void {
        const fonte = this.dados ?? [];
        this.filtros.forEach(filtro => {
            if (filtro.opcoes !== 'auto') return;
            const valores = fonte
                .map(item => this.extrairCampoValor(item, filtro))
                .filter(v => v !== null && v !== undefined && String(v).trim() !== '');
            this.opcoesDerivadas[filtro.id] = Array.from(
                new Set(valores.map(String))
            ).sort();
        });
    }

    private configurarSubscriptions(): void {
        this.filtros.forEach(filtro => {
            let stream = this.controles[filtro.id].valueChanges;

            if (filtro.tipo === 'text') {
                stream = stream.pipe(debounceTime(filtro.debounce ?? 300));
            }

            this.subs.push(
                stream.subscribe(() => {
                    this.resetarDependentes(filtro);
                    this.aplicarFiltros();
                })
            );
        });
    }

    /**
     * Reseta os filtros dependentes quando um filtro pai muda.
     * Os resets são feitos sem emitir evento (emitEvent: false) para
     * evitar disparar múltiplas execuções de aplicarFiltros.
     */
    private resetarDependentes(filtro: FiltroConfig): void {
        if (!filtro.resetaFiltros?.length) return;

        filtro.resetaFiltros.forEach(id => {
            const ctrl = this.controles[id];
            if (!ctrl) return;
            const tipo = this.filtros.find(f => f.id === id)?.tipo;
            ctrl.setValue(tipo === 'checkbox' ? false : '', { emitEvent: false });
        });
    }

    private aplicarFiltros(): void {
        const ativos: FiltrosAtivos = {};
        this.filtros.forEach(f => {
            ativos[f.id] = this.controles[f.id].value;
        });

        const resultado = (this.dados ?? []).filter(item =>
            this.filtros.every(filtro => this.passaFiltro(item, filtro, ativos[filtro.id]))
        );

        this.dadosFiltrados.emit(resultado);
        this.filtrosAtivos.emit(ativos);
    }

    private passaFiltro(item: any, filtro: FiltroConfig, valor: any): boolean {
        // Filtro inativo — deixa passar tudo
        if (filtro.tipo === 'checkbox' && valor === false) return true;
        if (
            filtro.tipo !== 'checkbox' &&
            (valor === '' || valor === null || valor === undefined)
        )
            return true;

        const campoValor = this.extrairCampoValor(item, filtro);

        if (filtro.tipo === 'text') {
            return String(campoValor ?? '')
                .toLowerCase()
                .includes(String(valor).toLowerCase());
        }

        if (filtro.tipo === 'checkbox') {
            const alvo =
                filtro.valorCheckbox !== undefined ? filtro.valorCheckbox : true;
            return campoValor === alvo;
        }

        // select: compara como string para compatibilidade com dados mistos
        return String(campoValor) === String(valor);
    }

    private extrairCampoValor(item: any, filtro: FiltroConfig): any {
        if (typeof filtro.campo === 'function') return filtro.campo(item);
        return item[filtro.campo];
    }
}
